import {Injectable, Injector} from "@angular/core";
import {EntityData, EntityModel, FedeltaImageData, field, identifier} from "@fedelta-pos/data-client";
import {SellableSizesData} from "./sellable-sizes-search.model";

export class ScheduleData extends EntityData<ScheduleData> {
    @identifier id: number;
    @field name: string;
}

export class MergedScheduleData {
    itemSchedules: ScheduleData[];
    exclItemSchedules: ScheduleData[];
    categorySchedules: ScheduleData[];
    exclCategorySchedules: ScheduleData[];
    primaryCategorySchedules: ScheduleData[];
    exclPrimaryCategorySchedules: ScheduleData[];
    submenuSchedules: ScheduleData[];
    exclSubmenuSchedules: ScheduleData[];
}

export class OrderTypeInfoData {
    orderType: "APP_AT_TABLE" | "TAKEAWAY" | "DELIVERY";
}

export class MenuSellableReferenceData {
    sellableId: number;
    integratedProductImage: FedeltaImageData;
}

export interface MenuItem {
    id: number;
    name: string;
    description: string;
    hidden: boolean;
    orderTypes: OrderTypeInfoData[];
    exclOrderTypes: OrderTypeInfoData[];
    filterTags: string;
    sellableSizeGroup: number;
    productImage: FedeltaImageData;
    sellable: MenuSellableReferenceData | MenuItemSellableData;
    mergedSchedules: MergedScheduleData[];
    stores: number[];
    exclStores: number[];
    membershipTypeIds: number[];
    includeGuests: boolean;
    exclMembershipTypeIds: number[];
    excludeGuests: boolean;
}

export enum MenuItemImageOverlay {
    REMOVE = <any>"REMOVE"
}

export enum MetaTagDefinitionType {
    // noinspection JSUnusedGlobalSymbols
    PRODUCT = <any>"PRODUCT"
}

export class MenuExtraItemsMenuItemData implements MenuItem {
    id: number;
    name: string;
    description: string;
    hidden: boolean;
    orderTypes: OrderTypeInfoData[];
    exclOrderTypes: OrderTypeInfoData[];
    filterTags: string;
    includeOptionOnUntaggedItems: boolean = true;
    tagItemOnOptionSelection: boolean;
    sellableSizeGroup: number;
    productImage: FedeltaImageData;
    productImageOverlay: MenuItemImageOverlay;
    sellable: MenuSellableReferenceData;
    mergedSchedules: MergedScheduleData[];
    stores: number[];
    exclStores: number[];
    membershipTypeIds: number[];
    includeGuests: boolean;
    exclMembershipTypeIds: number[];
    excludeGuests: boolean;
}

export class MenuPlgItemsMenuItemData implements MenuItem {
    id: number;
    name: string;
    description: string;
    hidden: boolean;
    orderTypes: OrderTypeInfoData[];
    exclOrderTypes: OrderTypeInfoData[];
    // noinspection JSUnusedGlobalSymbols
    filterExtras: boolean;
    filterTags: string;
    includeOptionOnUntaggedItems: boolean = true;
    // noinspection JSUnusedGlobalSymbols
    tagItemOnOptionSelection: boolean;
    sellableSizeGroup: number;
    productImage: FedeltaImageData;
    sellable: MenuSellableReferenceData;
    mergedSchedules: MergedScheduleData[];
    stores: number[];
    exclStores: number[];
    membershipTypeIds: number[];
    includeGuests: boolean;
    exclMembershipTypeIds: number[];
    excludeGuests: boolean;
}

export class MenuDietaryIndicatorsData {
    id: number;
    code: string;
    name: string;
}

export class MenuSellableCustomObjectData {
    // noinspection JSUnusedGlobalSymbols
    customObjectId: number;
    name: string;
    description: string;
    // noinspection JSUnusedGlobalSymbols
    customObjectTypeId: number;
}

export class MenuSellableCategoryData {
    // noinspection JSUnusedGlobalSymbols
    categoryId: number;
    name: string;
}

export class MenuSellableCategoryLookupData {
    ancestorId: number;
    category: MenuSellableCategoryData;
}

export class MenuSellablePrimaryCategoryData {
    categoryId: number;
    name: string;
    ancestors: MenuSellableCategoryLookupData[];
}

export class MenuSellableMetaTagDefinitionData {
    // noinspection JSUnusedGlobalSymbols
    metaTagDefinitionId: number;
    name: string;
    type: MetaTagDefinitionType;
}

export class MenuExtraProductLineData {
    sellableId: number;
    name: string;
    // noinspection JSUnusedGlobalSymbols
    kiloJoules: string;
    suppressForDecomposedProductLine: boolean = false;
    // noinspection JSUnusedGlobalSymbols
    externalDescription: string;
    // noinspection JSUnusedGlobalSymbols
    integratedExternalDescription: string;
    integratedProductImage: FedeltaImageData;
    // noinspection JSUnusedGlobalSymbols
    primaryCategory: MenuSellablePrimaryCategoryData;
    // noinspection JSUnusedGlobalSymbols
    dietaryIndicators: MenuDietaryIndicatorsData[];
    // noinspection JSUnusedGlobalSymbols
    customObjects: MenuSellableCustomObjectData[];
    tags: MenuSellableMetaTagDefinitionData[];
    // noinspection JSUnusedGlobalSymbols
    standardWaitTimeMinutes: number;
    // noinspection JSUnusedGlobalSymbols
    currentWaitTimeMinutes: number;
}

export class MenuProductExtraUsage {
    productLineId: number;
    parentExtraId: number;
}

export class MenuExtraProductData {
    id: string;
    price: number;
    qtyLimit: number;
    defaultOrderQty: number;
    // noinspection JSUnusedGlobalSymbols
    includeNestedExtras: boolean;
    productLine: MenuExtraProductLineData;

    /**
     * Assigned from the model to reference the respective menu item
     */
    menuItem: MenuExtraItemsMenuItemData;
}

export class MenuExtraGroupData {
    id: number;
    name: string;
    externalName: string;
    selectionMessage: string;
    promoted: string;
    promotionMessage: string;
    groupQtyMin: number;
    groupQtyLimit: number;
    totalIncluded: number;
    usages: MenuProductExtraUsage[];
    extraProducts: MenuExtraProductData[];
    parentExtraProductIds: string[];
}

export class MenuSellableData {
    sellableId: number;
    name: string;
    plu: string;
    kiloJoules: string;
    price: number;
    guestPrice: number;
    bonusPoints: number;
    minWebOrderQty: number;
    decomposedProductLine: boolean = false;
    externalDescription: string;
    applyMinimumAge: boolean = false;
    reorderableInApp: boolean;
    // noinspection JSUnusedGlobalSymbols
    allowZeroDollarInApp: boolean = false;
    promotedSellableId: number;
    integratedExternalDescription: string;
    integratedProductImage: FedeltaImageData;
    primaryCategory: MenuSellablePrimaryCategoryData;
    extras: MenuExtraGroupData[];
    dietaryIndicators: MenuDietaryIndicatorsData[];
    customObjects: MenuSellableCustomObjectData[];
    tags: MenuSellableMetaTagDefinitionData[];
    standardWaitTimeMinutes: number;
    currentWaitTimeMinutes: number;

    /**
     * Assigned from the model to reference the respective menu.
     */
    menuItem: MenuItemData;

    defaultPrice: number;
    defaultGuestPrice: number;
    extrasInitialised: boolean;
}

export interface MenuQtyData {
    indexOrder: number;
}

export class MenuProductLineQtyData implements MenuQtyData {
    indexOrder: number;
    productLine: MenuSellableData;
}

export class MenuPlgSellableData {
    defaultSellable: boolean;
    additionalPrice: number;
    sellable: MenuSellableData;

    /**
     * Assigned from the model to reference the respective menu item
     */
    menuItem: MenuPlgItemsMenuItemData;
}

export class MenuPlgData {
    plgId: number;
    name: string;
    externalName: string;
    sellableLines: MenuPlgSellableData[];
}

export class MenuProductLinePlgQtyData implements MenuQtyData {
    indexOrder: number;
    plg: MenuPlgData;
}

export class MenuCompositeData {
    sellableId: number;
    prodLineQuantities: MenuProductLineQtyData[];
    prodLinePlgQuantities: MenuProductLinePlgQtyData[];
}

export class MenuItemSellableData extends MenuSellableData {
    composite: MenuCompositeData;
}

export class MenuItemData implements MenuItem {
    id: number;
    name: string;
    description: string;
    pairingNotes: string;
    hidden: boolean;
    orderTypes: OrderTypeInfoData[];
    exclOrderTypes: OrderTypeInfoData[];
    filterExtras: boolean;
    filterPlgItems: boolean;
    filterTags: string;
    includeOptionOnUntaggedItems: boolean = true;
    tagItemOnOptionSelection: boolean;
    sellableSizeGroup: number;
    courseId: number;

    // Not used here, but declared for compatibility with MenuPlgItemsMenuItemData to allow a menu item from a normal menu item to be used for a plg item.
    productImage: FedeltaImageData;
    sellable: MenuItemSellableData;

    mergedSchedules: MergedScheduleData[];
    stores: number[];
    exclStores: number[];
    membershipTypeIds: number[];
    includeGuests: boolean;
    exclMembershipTypeIds: number[];
    excludeGuests: boolean;

    promotedFeatureTag: string;

    initialised: boolean;
    extrasInitialised: boolean;
    initialisationPending: boolean;
    sellableSize: SellableSizesData;
    hideWhenOrdered: boolean;
}

export class MenuCategoryData {
    id: number;
    name: string;
    description: string;
    categoryImage: FedeltaImageData;
    featured: boolean;
    hidden: boolean;
    orderTypes: OrderTypeInfoData[];
    exclOrderTypes: OrderTypeInfoData[];
    mergedSchedules: MergedScheduleData[];
    stores: number[];
    // noinspection JSUnusedGlobalSymbols
    exclStores: number[];
    // noinspection JSUnusedGlobalSymbols
    membershipTypeIds: number[];
    // noinspection JSUnusedGlobalSymbols
    includeGuests: boolean;
    // noinspection JSUnusedGlobalSymbols
    exclMembershipTypeIds: number[];
    // noinspection JSUnusedGlobalSymbols
    excludeGuests: boolean;
    menuItems: MenuItemData[];
    // noinspection JSUnusedGlobalSymbols
    standardWaitTimeMinutes: number;
    currentWaitTimeMinutes: number;

    hasHideWhenOrderedItems: boolean;
    itemFeatureTag: string;

    pairingsCategory: boolean;
}

export class MenuPlgItemsData {
    id: number;
    name: string;
    menuItems: MenuPlgItemsMenuItemData[];
}

export class MenuExtraItemsData {
    id: number;
    name: string;
    menuItems: MenuExtraItemsMenuItemData[];
}

export class CountdownData {
    sellableId: number;
    qtyAvailable: number;
}

export class PairingProductData {
    sellableId: number;
    name: string;
}

export class PairingData {
    product: PairingProductData;
    notes: string;
    index: number;
    pairingSet: PairingSetData;
}

export class PairedItemData {
    product: PairingProductData;
    pairings: PairingData[];
}

export class PairingSetData {
    id: number;
    name: string;
    targetCategory: string;
    categoryTitle: string;
    categoryDescription: string;
    schedules: ScheduleData[];
    pairedItems: PairedItemData[];
}

export class MenuData extends EntityData<MenuData> {
    id: number;
    name: string;
    useSizeGroupNames: boolean;
    hideOutOfStock: boolean;
    defaultExtrasLimit: number;
    showItemImages: boolean;
    showMobileItemImages: boolean;
    showCategoryImages: boolean;
    showSubcategoryImages: boolean;
    showMobileCategoryImages: boolean;
    showMobileSubcategoryImages: boolean;
    defaultItemImage: FedeltaImageData;
    defaultCategoryImage: FedeltaImageData;
    pageImage: FedeltaImageData;
    pageImageOpacity: number;
    pageImageTextColor: string;
    pageImageBackgroundColor: string;
    categories: MenuCategoryData[];
    extraItems: MenuExtraItemsData[];
    plgItems: MenuPlgItemsData[];
    extras: MenuExtraGroupData[];
    plgs: MenuPlgData[];
    countdowns: CountdownData[];
    pairingSets: PairingSetData[];
}

// noinspection GrazieInspection, AiaStyle
@Injectable()
export class MenuEntityModel extends EntityModel<MenuData> {

    constructor(injector: Injector) {
        console.log("--- MenuEntityModel");

        super(injector, MenuData);
    }

    /**
     * For performance, serialization to EntityData has been disabled for this model
     */
    protected getSerializeToEntityData(): boolean {
        return false;
    }
}



// WEBPACK FOOTER //
// ./src/app/menu/menu-entity.model.ts